import React from 'react';
import { MapPin, Phone, Mail, Clock, Facebook, Linkedin, Instagram } from 'lucide-react';
import logo from '../assets/logo.png';

const Footer = () => {
  const branches = [
    {
      name: 'Headquarters - Firq, Nizwa',
      address: 'Byth Firq building, Near Badar al Sama Hospital, Firq, Nizwa, Oman',
      phone: '+968 9178 0974',
      hours: [
        'Sun - Thu & Sat: 8:00 AM - 1:00 PM & 4:00 PM - 10:00 PM',
        'Fri: 9:00 AM - 12:00 PM & 4:30 PM - 9:30 PM',
      ],
    },
    {
      name: 'Branch - Al Khadra, Nizwa',
      address: 'Shop 18, Near Nizwa Sports Club Main Entrance, Timsa-Karsha Road, Oman',
      phone: '+968 7191 8242',
      hours: [
        'Sun - Thu & Sat: 8:30 AM - 1:30 PM & 4:30 PM - 9:30 PM',
        'Fri: 4:30 PM - 9:30 PM',
      ],
    },
  ];

  return (
    <footer id="contact" className="bg-gradient-to-b from-gray-900 to-black text-white pt-16 pb-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-12">
          {/* Company Info */}
          <div className="space-y-6">
            <img src={logo} alt="Zuhoor Nizwa" className="h-24 w-auto" />
            <p className="text-gray-400 leading-relaxed">
              Your trusted travel partner in Nizwa, offering comprehensive travel solutions from international holiday packages to local services.
            </p>
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5 text-[#F4B41A]" />
              <a href="mailto:FIRQ.BRANCH@ZUHOORNTRAVEL.COM" className="text-gray-300 hover:text-[#F4B41A] transition-colors duration-300">
                FIRQ.BRANCH@ZUHOORNTRAVEL.COM
              </a>
            </div>
            
            {/* Social Media Icons */}
            <div className="pt-4">
              <h4 className="text-lg font-semibold text-white mb-4">Follow Us</h4>
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.facebook.com/ZuhoorNizwaTravels/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  title="Facebook"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center transition-all duration-300 group-hover:bg-[#1877F2] group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-[#1877F2]/50">
                    <Facebook className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors duration-300" />
                  </div>
                </a>
                <a
                  href="https://www.linkedin.com/company/zuhoor-nizwa-travels-and-tourism"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  title="LinkedIn"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center transition-all duration-300 group-hover:bg-[#0A66C2] group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-[#0A66C2]/50">
                    <Linkedin className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors duration-300" />
                  </div>
                </a>
                <a
                  href="https://www.instagram.com/zuhoor_nizwa_travels"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  title="Instagram"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center transition-all duration-300 group-hover:bg-gradient-to-br group-hover:from-[#833AB4] group-hover:via-[#FD1D1D] group-hover:to-[#F77737] group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-pink-500/50">
                    <Instagram className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors duration-300" />
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Branch Locations */}
          {branches.map((branch, index) => (
            <div key={index} className="space-y-4">
              <h3 className="text-xl font-bold text-[#F4B41A] mb-4">{branch.name}</h3>
              
              {/* Address */}
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-[#1E7B8C] mt-1 flex-shrink-0" />
                <p className="text-gray-300 text-sm leading-relaxed">{branch.address}</p>
              </div>

              {/* Phone */}
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-[#1E7B8C] flex-shrink-0" />
                <a
                  href={`tel:${branch.phone.replace(/\s/g, '')}`}
                  className="text-gray-300 hover:text-[#F4B41A] transition-colors duration-300 font-medium"
                >
                  {branch.phone}
                </a>
              </div>

              {/* Hours */}
              <div className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-[#1E7B8C] mt-1 flex-shrink-0" />
                <div className="text-gray-400 text-sm space-y-1">
                  {branch.hours.map((hour, idx) => (
                    <p key={idx}>{hour}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <p className="text-sm text-gray-400 text-center">
              <span className="font-semibold text-[#F4B41A]">Important Notice:</span> Zuhoor Nizwa Travels & Tourism operates exclusively from our two locations in Firq and Al Khadra, Nizwa. We do not own or operate any branches in Seeb or any other location.
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} Zuhoor Nizwa Travels & Tourism. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm">
              Website: <span className="text-[#F4B41A] font-medium">zuhorntravel.com</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;